import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const costTypes = [
  {
    label: 'Forex Spread',
    href: '/forex-spread/',
    desc: 'Bid-ask cost before entering a trade',
  },
  {
    label: 'Forex Commission',
    href: '/forex-commission/',
    desc: 'Separate trading fee on some account types',
  },
  {
    label: 'Forex Swap',
    href: '/forex-swap/',
    desc: 'Overnight cost or credit for held positions',
  },
  {
    label: 'Forex Slippage',
    href: '/forex-slippage/',
    desc: 'Execution difference during fast markets',
  },
  {
    label: 'Hidden Costs',
    href: '/hidden-forex-trading-costs/',
    desc: 'Extra fees and indirect trading costs',
  },
];

export default function Header() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 16);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 150);
  };

  const isActive = (path: string) => location.pathname === path;
  const isCostTypeActive = ['/forex-spread/', '/forex-commission/', '/forex-swap/', '/forex-slippage/', '/hidden-forex-trading-costs/'].includes(location.pathname);

  return (
    <header
      className={`sticky top-0 z-[1000] border-b border-[#E6EAF0] transition-all duration-300 ${
        scrolled
          ? 'bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)]'
          : 'bg-white/92 backdrop-blur-md'
      }`}
    >
      <div className="w-full max-w-main mx-auto px-4 md:px-6 h-[64px] md:h-[72px] flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center no-underline">
          <img src="/images/logo.png" alt="FXCostGuide" className="h-[40px] w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-4 py-2 text-[15px] font-medium rounded-pill whitespace-nowrap transition-colors ${
              isActive('/')
                ? 'text-primary-600 bg-primary-50'
                : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
            }`}
          >
            Costs Overview
          </Link>

          {/* Cost Types Dropdown */}
          <div
            className="relative"
            ref={dropdownRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className={`px-4 py-2 text-[15px] font-medium rounded-pill whitespace-nowrap transition-colors flex items-center gap-1 ${
                dropdownOpen || isCostTypeActive
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              Cost Types
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[360px] bg-white border border-[#D9E2EC] rounded-[20px] p-3 shadow-dropdown z-50">
                {costTypes.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`block px-4 py-3 rounded-[14px] transition-colors no-underline group ${
                        active ? 'bg-primary-50' : 'hover:bg-background-100'
                      }`}
                    >
                      <div className={`text-[15px] font-semibold transition-colors ${
                        active ? 'text-primary-700' : 'text-foreground-950 group-hover:text-primary-600'
                      }`}>
                        {item.label}
                      </div>
                      <div className={`text-[13px] mt-0.5 leading-snug ${
                        active ? 'text-primary-600' : 'text-foreground-500'
                      }`}>
                        {item.desc}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          <Link
            to="/spread-vs-commission/"
            className={`px-4 py-2 text-[15px] font-medium rounded-pill whitespace-nowrap transition-colors ${
              isActive('/spread-vs-commission/')
                ? 'text-primary-600 bg-primary-50'
                : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
            }`}
          >
            Spread vs Commission
          </Link>

          <Link
            to="/calculate-forex-trading-costs/"
            className={`px-4 py-2 text-[15px] font-medium rounded-pill whitespace-nowrap transition-colors ${
              isActive('/calculate-forex-trading-costs/')
                ? 'text-primary-600 bg-primary-50'
                : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
            }`}
          >
            Calculate Costs
          </Link>

          <Link
            to="/forex-trading-costs-by-strategy/"
            className={`px-4 py-2 text-[15px] font-medium rounded-pill whitespace-nowrap transition-colors ${
              isActive('/forex-trading-costs-by-strategy/')
                ? 'text-primary-600 bg-primary-50'
                : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
            }`}
          >
            Cost by Strategy
          </Link>
          <Link
            to="/about/"
            className={`px-4 py-2 text-[15px] font-medium rounded-pill whitespace-nowrap transition-colors ${
              isActive('/about/')
                ? 'text-primary-600 bg-primary-50'
                : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
            }`}
          >
            About Us
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-[42px] h-[42px] rounded-[12px] flex items-center justify-center border border-[#D9E2EC] cursor-pointer transition-colors hover:bg-background-100"
          style={{ backgroundColor: '#F2F6FC' }}
          aria-label="Menu"
        >
          <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl text-foreground-950`}></i>
        </button>
      </div>

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-[#E6EAF0] shadow-dropdown">
          <div className="px-4 py-4 flex flex-col gap-1">
            <Link
              to="/"
              className={`px-4 py-3 text-[15px] font-semibold rounded-[12px] transition-colors no-underline ${
                isActive('/') ? 'text-primary-700 bg-primary-50' : 'text-foreground-950 hover:bg-background-100'
              }`}
            >
              Costs Overview
            </Link>
            <div className="px-4 py-2 text-[13px] font-semibold text-foreground-500 uppercase tracking-wide">
              Cost Types
            </div>
            {costTypes.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`px-4 py-3 text-[15px] font-medium rounded-[12px] transition-colors no-underline ${
                  isActive(item.href)
                    ? 'text-primary-700 bg-primary-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="h-px bg-[#E6EAF0] my-2"></div>
            <Link
              to="/spread-vs-commission/"
              className={`px-4 py-3 text-[15px] font-semibold rounded-[12px] transition-colors no-underline ${
                isActive('/spread-vs-commission/') ? 'text-primary-700 bg-primary-50' : 'text-foreground-950 hover:bg-background-100'
              }`}
            >
              Spread vs Commission
            </Link>
            <Link
              to="/calculate-forex-trading-costs/"
              className={`px-4 py-3 text-[15px] font-semibold rounded-[12px] transition-colors no-underline ${
                isActive('/calculate-forex-trading-costs/') ? 'text-primary-700 bg-primary-50' : 'text-foreground-950 hover:bg-background-100'
              }`}
            >
              Calculate Costs
            </Link>
            <Link
              to="/forex-trading-costs-by-strategy/"
              className={`px-4 py-3 text-[15px] font-semibold rounded-[12px] transition-colors no-underline ${
                isActive('/forex-trading-costs-by-strategy/') ? 'text-primary-700 bg-primary-50' : 'text-foreground-950 hover:bg-background-100'
              }`}
            >
              Trading Costs by Strategy
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}